import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select } from "antd";
import { debounce } from "../../../helpers/utility";
import { MedicineOptionRow } from "../../common/MedicineStockTag";
import styles from "./pharmacySearch.module.css";

const AsyncSearchSelect = ({
  fetcher,
  value,
  onChange,
  placeholder,
  initialOption,
  allowClear = true,
  loadOnOpen = false,
  disabled,
}) => {
  const [options, setOptions] = useState(initialOption ? [initialOption] : []);
  const [loading, setLoading] = useState(false);
  const [typed, setTyped] = useState("");
  const optionMapRef = useRef(new Map());
  const requestIdRef = useRef(0);
  /*
   * Callers that need an argument bound - fetcher={(t) => fetchX(t, id)} -
   * hand over a new function every render. Keying the debounce on `fetcher`
   * rebuilt it each time, which threw away the pending timer and put one
   * request on the wire per keystroke; the ref keeps the debounce intact.
   */
  const fetcherRef = useRef(fetcher);

  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  const rememberOptions = (opts) => {
    const next = new Map(optionMapRef.current);
    (opts || []).forEach((opt) => {
      if (opt?.value != null) next.set(opt.value, opt);
    });
    optionMapRef.current = next;
  };

  useEffect(() => {
    if (initialOption) {
      rememberOptions([initialOption]);
      setOptions((prev) =>
        prev.some((o) => o.value === initialOption.value)
          ? prev
          : [initialOption, ...prev]
      );
    }
  }, [initialOption?.value]);

  const handleSearch = (text) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    fetcherRef.current(text)
      .then((opts) => {
        if (requestIdRef.current !== requestId) return; // superseded
        const list = opts || [];
        rememberOptions(list);
        setOptions(list);
      })
      .catch(() => {
        if (requestIdRef.current === requestId) setOptions([]);
      })
      .finally(() => {
        if (requestIdRef.current === requestId) setLoading(false);
      });
  };

  const optimizedSearch = useMemo(() => debounce(handleSearch, 300), []);

  const handleOpen = (open) => {
    if (!open || !loadOnOpen) return;
    const onlyPreselected =
      options.length === 0 ||
      (options.length === 1 && options[0]?.value === value);
    if (onlyPreselected) handleSearch("");
  };

  return (
    <Select
      showSearch
      onDropdownVisibleChange={handleOpen}
      allowClear={allowClear}
      disabled={disabled}
      value={value}
      placeholder={placeholder}
      filterOption={false}
      loading={loading}
      onSearch={(text) => {
        setTyped(text);
        optimizedSearch(text);
      }}
      onChange={(nextValue, option) => {
        const full = optionMapRef.current.get(nextValue) || option || null;
        onChange?.(nextValue, full);
      }}
      options={options}
      /*
       * The dropdown row carries the stock badge while the closed select keeps
       * the plain label, so picking a medicine does not drag the badge into
       * the field. Options without stock fields (vendors, batches) render the
       * label untouched.
       */
      optionRender={(option) => (
        <MedicineOptionRow label={option.label} medicine={option.data} />
      )}

      notFoundContent={
        loading
          ? "Searching..."
          : !loadOnOpen && !typed
          ? "Type to search"
          : "No results"
      }
      popupMatchSelectWidth={false}
      popupClassName={styles.dropdown}
      style={{ width: "100%" }}
    />
  );
};

export default AsyncSearchSelect;
